import { logWarning } from '@databases/logs';
import {
  type DatabaseField,
  DatabaseFieldType,
  type NumberFormat,
  NumberFormats,
  NumberSeparators,
  type RawDatabaseValue,
  RenderContext,
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';

import { getTextContent } from '../../lib/dom';
import { encodeHtml } from '../../utils';
import type { FieldType } from '../field-types';

const MINUS = '\u2212';

export function formatNumber(
  value: string | undefined,
  numberFormat: NumberFormat
): string {
  const {
    minimumFractionDigits,
    maximumFractionDigits,
    decimalPlaces,
    decimalSeparator,
    thousandsSeparator,
  } = numberFormat;
  let formattedValue = extractNumber(value);

  if (!formattedValue) {
    return '';
  }

  if (numberFormat.format === NumberFormats.PERCENT) {
    const numValue = parseFloat(formattedValue);
    if (!isNaN(numValue)) {
      const percentValue = parseFloat((numValue * 100).toFixed(10));
      formattedValue = String(percentValue);
    }
  }

  let { prefix, suffix } = numberFormat;
  let integerValue = formattedValue?.split(NumberSeparators.DOT)[0];
  let decimalValue = formattedValue?.split(NumberSeparators.DOT)[1] ?? '';

  integerValue = integerValue.replace(/^-/, MINUS);

  if (
    prefix &&
    (numberFormat.format === NumberFormats.CURRENCY ||
      numberFormat.format === NumberFormats.CUSTOM)
  ) {
    prefix = `${prefix}${NumberSeparators.WHITESPACE}`;
  } else {
    prefix = '';
  }

  if (
    suffix &&
    (numberFormat.format === NumberFormats.PERCENT ||
      numberFormat.format === NumberFormats.CUSTOM)
  ) {
    suffix = `${NumberSeparators.WHITESPACE}${suffix}`;
  } else {
    suffix = '';
  }
  if (decimalPlaces !== undefined) {
    const places = decimalPlaces;

    const characterCount = decimalValue?.length || 0;
    const deltaCount = characterCount < places ? places - characterCount : 0;
    decimalValue = `${decimalValue}${'0'.repeat(deltaCount)}`;

    decimalValue = decimalValue?.slice(0, places);
  } else {
    if (minimumFractionDigits !== undefined) {
      const characterCount = decimalValue?.length || 0;
      const deltaCount =
        characterCount < minimumFractionDigits
          ? minimumFractionDigits - characterCount
          : 0;
      decimalValue = `${decimalValue}${'0'.repeat(deltaCount)}`;
    }
    if (maximumFractionDigits !== undefined) {
      decimalValue = decimalValue?.slice(0, maximumFractionDigits);
    }
  }

  if (thousandsSeparator) {
    integerValue = integerValue.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
  }

  const combinedValue = `${integerValue}${
    decimalValue ? `${decimalSeparator}${decimalValue}` : ''
  }`;

  return `${prefix}${combinedValue}${suffix}`;
}

/**
 * Finds the first number in the given text, useful when converting a text field into a number field.
 */
export function extractNumber(text: string | undefined): string {
  const dash = '-';
  const m = text?.match(/-?[0-9]*[.,]?[0-9]+/);

  let extracted = '';

  if (m) {
    extracted = m[0].replace(NumberSeparators.COMMA, NumberSeparators.DOT);
    const startsWithSeparator = extracted.startsWith(NumberSeparators.DOT);
    const startsWithNegativeNumberAndSeparator = extracted.startsWith(
      `${dash}${NumberSeparators.DOT}`
    );
    extracted = startsWithSeparator
      ? extracted.replace(NumberSeparators.DOT, `0${NumberSeparators.DOT}`)
      : startsWithNegativeNumberAndSeparator
      ? extracted.replace(
          `${dash}${NumberSeparators.DOT}`,
          `${dash}0${NumberSeparators.DOT}`
        )
      : extracted;
  }

  return extracted;
}

export function getNumberDisplayString(
  value: string,
  field: DatabaseField,
  locale: string
) {
  switch (field.settings.numberFormat.format) {
    case NumberFormats.CUSTOM:
      return formatNumber(value, field.settings.numberFormat);
    case NumberFormats.NUMBER: {
      try {
        return formatNumber(value, field.settings.numberFormat);
      } catch (e) {
        if (e instanceof Error) {
          logWarning('Unsupported number format', e);
        }
        return '';
      }
    }
    case NumberFormats.PERCENT:
      return formatNumber(value, {
        ...field.settings.numberFormat,
        suffix: '%',
      });
    case NumberFormats.CURRENCY:
      return formatNumber(value, field.settings.numberFormat);
    case NumberFormats.DOLLAR:
      return Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(
        Number(value)
      );
    case NumberFormats.EURO:
      return Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(
        Number(value)
      );
  }
}

function getStringRepresentation(context: {
  field: DatabaseField;
  value: RawDatabaseValue<DatabaseFieldType.NUMBER>;
}): string {
  return context.value != null ? `${context.value}` : '';
}

function getNumberRepresentation(context: {
  field: DatabaseField;
  value: RawDatabaseValue<DatabaseFieldType.NUMBER>;
}): number | undefined {
  const extractedNumber = extractNumber(context.value);
  const result = Number(extractedNumber);
  return !isNaN(result) ? result : undefined;
}

const numberFieldType: FieldType<DatabaseFieldType.NUMBER> = {
  supportedFilterTypes: [
    DatabaseFilterType.EMPTY,
    DatabaseFilterType.LESS,
    DatabaseFilterType.GREATER,
    DatabaseFilterType.EQUALS,
  ],

  isCsvImportSupported: true,

  renderExportView: ({ value, field }, state, renderContext) => {
    switch (renderContext) {
      case RenderContext.COPY_DATABASE_TO_CLIPBOARD:
      case RenderContext.EXPORT_DATABASE:
        // When exporting the database, do not apply formatting settings, so that the number is properly recognized as a number
        // wherever it is imported/pasted.
        return encodeHtml(getStringRepresentation({ field, value }));

      default:
        return encodeHtml(formatNumber(value, field.settings.numberFormat));
    }
  },

  parseFromHtml: (el) => extractNumber(getTextContent(el)),

  getInitialValue: () => '',

  isEmptyValue: ({ value }) => !value,

  isEqualValue: ({ value: value1 }, { value: value2 }) => value1 === value2,

  getStringRepresentation,

  getNumberRepresentation,

  convertFrom: {
    [DatabaseFieldType.TEXT]: ({ value }) => extractNumber(value),
  },

  convertTo: {
    [DatabaseFieldType.TEXT]: getStringRepresentation,
  },
};

export default numberFieldType;
